import type { PartialMessage } from '@bufbuild/protobuf'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import {
  businessSignup as signupBusinessDescriptor,
  signup as signupDescriptor,
} from '@jotta/grpc-connect-openapi/signupQuery'
import { useGetConfig } from '@jotta/grpc-connect-client/config'
import { getCurrentCountry } from '@jotta/grpc-connect-openapi/geoQuery'
import type { SignupResponse } from '@jotta/grpc-connect-openapi/signup'
import {
  BusinessSignupRequest,
  SignupRequest,
} from '@jotta/grpc-connect-openapi/signup'
import { generateSignupToken } from './signupForm'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { autoLogin } from '@jotta/utils/auth'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { Trans } from '@lingui/macro'
import clsx from 'clsx'
import { useCallback, useEffect, useState } from 'react'
import { CreateBusinessUser } from './CreateBusinessUser'
import { CreatePrivateUser } from './CreatePrivateUser'
import { CreateVoucherUser } from './CreateVoucherUser'
import { CreatePreSignupUser } from './CreatePreSignupUser'
import { useCurrentLanguage } from '@jotta/i18n'
import type { FederationIntent } from './Federation'
import { Federation } from './Federation'
import { OrDivider } from '@jotta/ui/Divider'
import { FederatedSignupConfirmation } from './FederatedSignupConfirmation'
import type { LoggedIn } from '@jotta/grpc-connect-openapi/login'
import { ModalDialog } from '@jotta/ui/ModalDialog'
import { ErrorMessage } from '@jotta/ui/ErrorMessage'
import { Feature } from '@jotta/grpc-connect-openapi/feature'
import { useMutation } from '@tanstack/react-query'
import { mutate } from '@jotta/grpc-connect-client/util'
import { publicTransport } from '@jotta/grpc-connect-client/transport'
import type { Country } from '@jotta/grpc-connect-openapi/country'
import { useAuthStore } from './useAuthStore'
import { cn } from '@jotta/utils/css'
import { persistInstallationCode } from './installationCodeStore'
import { AppPromotionDrawer } from './AppPromotionDrawer'
import { getLogin } from '@jotta/grpc-connect-openapi/loginQuery'
import { useBrandCode } from '@jotta/ui/useBrandTheme'

export type UserType =
  | 'private'
  | 'business'
  | 'voucher'
  | {
      case: 'presignup'
      phoneNumber: string
    }

type SignupResult = {
  installationCode?: string
  redirectUrl?: string
  autoLogin?: LoggedIn
}

export type SignupRequestData = {
  request: PartialMessage<SignupRequest>['request']
  voucherCode?: string
  customerGroupCode?: string
  wantsNewsletter: boolean
}
export type SignupRequestCallback = (
  request: SignupRequestData,
) => Promise<SignupResponse>

const userAgent = navigator.userAgent
const isIos = /iPad|iPhone|iPod/.test(userAgent)
const isAndroid = /android/i.test(userAgent)

const usePromoteApp = (
  allow: boolean,
  loginToken: string | undefined,
): boolean => {
  const disallowed = !allow || !(isIos || isAndroid)

  const brandCode = useBrandCode()
  const { data } = useConnectQuery(
    getLogin,
    {
      loginToken,
      brandCode,
    },
    {
      enabled: Boolean(loginToken) && !disallowed,
    },
  )

  if (disallowed) {
    return false
  } else if (!loginToken) {
    return true
  }

  const result = data?.result
  switch (result?.case) {
    case undefined:
    case 'notFound':
      return false
    case 'login':
      return result.value.clientId === 'web'
    default:
      return exhaustiveGuard(result)
  }
}

export function CreateUser({
  type,
  allowAppPromotion,
  federationIntent,
  loginToken,
  className,
  keepRouteOnSuccess,
  countryOverride,
  customerGroupOverride,
  familyInviteId,
  prefill,
  ctaLabelText,
  onClose,
  onRequestLogin,
}: {
  type: UserType
  allowAppPromotion: boolean
  federationIntent?: FederationIntent
  loginToken?: string
  className?: string
  keepRouteOnSuccess?: boolean
  customerGroupOverride?: string
  countryOverride?: Country
  prefill?: { name?: string; email?: string; voucherCode?: string }
  familyInviteId?: string
  ctaLabelText?: string
  onClose?: () => void
  onRequestLogin?: () => void
}) {
  const { federationToken, setFederationToken } = useAuthStore()
  const [signupResult, setSignupResult] = useState<SignupResult>()
  const [appPromotionDeclined, setAppPromotionDeclined] = useState(false)
  const promoteApp = usePromoteApp(
    allowAppPromotion && !appPromotionDeclined,
    loginToken,
  )

  const language = useCurrentLanguage()
  const { data: getConfigResponse } = useGetConfig(customerGroupOverride)
  const brandingInfo = getConfigResponse?.brandingInfo
  const customerGroupCode =
    customerGroupOverride || brandingInfo?.customerGroupCode
  const supportsVoucher =
    !familyInviteId &&
    Boolean(getConfigResponse?.activeFeatures?.includes(Feature.VOUCHER_SIGNUP))

  const { data: currentCountry } = useConnectQuery(
    getCurrentCountry,
    {},
    {
      throwOnError: false,
      select: data => data.country,
    },
  )
  const country = countryOverride || currentCountry

  const {
    data: signupPrivateResponse,
    mutateAsync: signupPrivate,
    error: signupPrivateError,
    isPending: isPrivatePending,
  } = useMutation({
    throwOnError: false,
    mutationFn: async ({
      request,
      wantsNewsletter,
      voucherCode,
      customerGroupCode: requestedCustomerGroupCode,
    }: SignupRequestData) =>
      mutate(
        signupDescriptor,
        await generateSignupToken(
          new SignupRequest({
            customerGroupCode: requestedCustomerGroupCode || customerGroupCode,
            language,
            country,
            voucherCode,
            familyInviteId,
            wantsNewsletter,
            request,
            loginToken,
            generateInstallationCode: promoteApp,
          }),
          (message, token) => (message.token = token),
        ),
        publicTransport,
      ),
  })

  const {
    mutateAsync: signupBusiness,
    data: signupBusinessResponse,
    error: signupBusinessError,
    isPending: isBusinessPending,
  } = useMutation({
    throwOnError: false,
    mutationFn: async (req: PartialMessage<BusinessSignupRequest>) =>
      mutate(
        signupBusinessDescriptor,
        await generateSignupToken(
          new BusinessSignupRequest({
            customerGroupCode,
            language,
            country,
            ...req,
          }),
          (req, token) => (req.token = token),
        ),
        publicTransport,
      ),
  })

  const redirectTo = keepRouteOnSuccess
    ? location.href
    : `${location.origin}/web/sync`

  const onSuccess = useCallback(
    (loggedIn: LoggedIn) =>
      setSignupResult({
        autoLogin: loggedIn,
      }),
    [setSignupResult],
  )

  useEffect((): undefined => {
    if (signupPrivateResponse) {
      const signupPrivateResult = signupPrivateResponse.result
      switch (signupPrivateResult?.case) {
        case undefined:
          break
        case 'success': {
          const success = signupPrivateResult.value
          setSignupResult({
            autoLogin: success,
            installationCode: success.installationCode,
          })
          break
        }

        case 'redirect': {
          const redirect = signupPrivateResult.value
          setSignupResult({
            redirectUrl: redirect.url,
            installationCode: redirect.installationCode,
          })
          break
        }
        case 'emailInUse':
        case 'signupNotSupported':
          throw new Error('signup failed')
        default:
          return exhaustiveGuard(signupPrivateResult)
      }
    }
  }, [setSignupResult, signupPrivateResponse])

  useEffect((): undefined => {
    if (signupBusinessResponse) {
      const signupBusinessResult = signupBusinessResponse.result
      switch (signupBusinessResult?.case) {
        case 'success':
          onSuccess(signupBusinessResult.value)
          break
        case undefined:
        case 'emailInUse':
        case 'signupNotSupported':
          throw new Error('signup failed')
        default:
          return exhaustiveGuard(signupBusinessResult)
      }
    }
  }, [onSuccess, signupBusinessResponse])

  const downloadMobileAppLink =
    !signupResult || !promoteApp
      ? undefined
      : isIos
        ? brandingInfo?.downloadIosLink
        : isAndroid
          ? brandingInfo?.downloadAndroidLink
          : undefined

  const actionableSignupResult = !downloadMobileAppLink
    ? signupResult
    : undefined

  // After a successful signup, we receive the means to log the user in either by following a redirect to complete an OIDC flow or direct auto-login.
  // This should happen automatically unless we are promoting the mobile apps.
  useEffect(() => {
    if (actionableSignupResult) {
      const autoLoginLogin = actionableSignupResult.autoLogin
      const redirectUrl = actionableSignupResult.redirectUrl
      if (autoLoginLogin) {
        autoLogin({
          username: autoLoginLogin.username,
          token: autoLoginLogin.autoLoginCode,
          redirectTo: redirectTo,
        })
      } else if (redirectUrl) {
        window.location.assign(redirectUrl)
      } else {
        throw new Error('no action found for successful signup')
      }
    }
  }, [actionableSignupResult, redirectTo])

  if (!customerGroupCode || actionableSignupResult) {
    return <LoadingOverlay open />
  }

  const ctaClassName = cn({
    'self-start': !onClose,
  })

  const baseLayoutClass = 'flex flex-col gap-4 w-full'
  return (
    <div className={clsx(baseLayoutClass, className)}>
      {type === 'private' &&
        federationIntent &&
        getConfigResponse?.viewConfig?.showLinkedAccounts && (
          <>
            <Federation intent={federationIntent} />
            <OrDivider />
          </>
        )}
      {(() => {
        if (typeof type !== 'string') {
          return (
            <CreatePreSignupUser
              phoneNumber={type.phoneNumber}
              customerGroupCode={customerGroupCode}
              className={baseLayoutClass}
              isPending={isPrivatePending}
              signup={signupPrivate}
            />
          )
        }

        switch (type) {
          case 'voucher':
            return (
              <CreateVoucherUser
                customerGroupCode={customerGroupCode}
                className={baseLayoutClass}
                onRequestLogin={onRequestLogin}
                isPending={isPrivatePending}
                signup={signupPrivate}
                prefill={prefill}
              />
            )
          case 'private':
            return (
              <CreatePrivateUser
                className={baseLayoutClass}
                ctaClassName={ctaClassName}
                ctaLabelText={ctaLabelText}
                onRequestLogin={onRequestLogin}
                isPending={isPrivatePending}
                supportsVoucher={supportsVoucher}
                signup={signupPrivate}
                prefill={{
                  ...prefill,
                  customerGroupCode,
                }}
              />
            )
          case 'business':
            return (
              <CreateBusinessUser
                className={baseLayoutClass}
                ctaClassName={ctaClassName}
                ctaLabelText={ctaLabelText}
                isPending={isBusinessPending}
                supportsVoucher={supportsVoucher}
                signup={signupBusiness}
                prefill={{
                  ...prefill,
                  customerGroupCode,
                }}
              />
            )
          default:
            exhaustiveGuard(type)
        }
      })()}

      {onClose ? (
        <button
          className="btn btn-ghost w-full max-w-[309px] self-center"
          onClick={onClose}
        >
          <Trans>Cancel</Trans>
        </button>
      ) : (
        <p className="self-center text-grey4">
          <Trans>Already have an account?</Trans>&nbsp;
          <a href="/api/login" className="!text-signup-link underline">
            <Trans>Log in</Trans>
          </a>
        </p>
      )}

      <ErrorMessage error={signupPrivateError} />

      <ErrorMessage error={signupBusinessError} />

      <ModalDialog open={Boolean(federationToken)}>
        {federationToken && (
          <FederatedSignupConfirmation
            className="px-8 py-10"
            customerGroupCode={customerGroupCode}
            federationToken={federationToken}
            onSuccess={onSuccess}
            onCancel={() => setFederationToken(undefined)}
            country={country}
            language={language}
            ctaLabelText={ctaLabelText}
          />
        )}
      </ModalDialog>

      <AppPromotionDrawer
        open={Boolean(downloadMobileAppLink)}
        downloadMobileAppLink={downloadMobileAppLink || ''}
        onLinkClick={() => {
          persistInstallationCode(signupResult?.installationCode)

          // We will be redirected and probably trigger an OS app switch leaving the browser frozen in the background.
          // Abort the login process first to ensure a decent app state
          setSignupResult(undefined)
          setAppPromotionDeclined(true)
          if (onClose) {
            onClose()
          }
        }}
        onClose={() => setAppPromotionDeclined(true)}
      />
    </div>
  )
}
